/**
 * ProspectsList — tabular view of prospects with filters + search + pagination.
 *
 * Same data as the kanban but in a vertically scannable table. Admin can:
 *   - Filter by stage / status / affiliate / archived / converted toggles
 *   - Search by name, email, or business name
 *   - Click any row to open ProspectDetail
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, RefreshCw, Search } from 'lucide-react';
import { getProspects, getStages } from './api';
import type { ProspectListRow, Stage, StageStatus } from './types';

interface Props {
  onOpenProspect: (prospectId: string) => void;
}

const PAGE_SIZE = 50;

const STATUS_OPTIONS: Array<{ value: '' | StageStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'awaiting_client', label: 'Awaiting client' },
  { value: 'awaiting_admin', label: 'Awaiting us' },
  { value: 'awaiting_external', label: 'Awaiting review' },
  { value: 'rejected_needs_fix', label: 'Needs fix' },
  { value: 'complete', label: 'Complete' },
];

export default function ProspectsList({ onOpenProspect }: Props) {
  const [rows, setRows] = useState<ProspectListRow[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [stageId, setStageId] = useState('');
  const [status, setStatus] = useState<'' | StageStatus>('');
  const [showArchived, setShowArchived] = useState(false);
  const [showConverted, setShowConverted] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [stageId, status, showArchived, showConverted, debouncedSearch]);

  // Load stages once for the filter dropdown
  useEffect(() => {
    (async () => {
      const res = await getStages();
      if (res.success && res.data) setStages(res.data);
    })();
  }, []);

  // Load prospects whenever filters or page change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await getProspects({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        stage_id: stageId || undefined,
        status: status || undefined,
        archived: showArchived || undefined,
        converted: showConverted || undefined,
        search: debouncedSearch || undefined,
      });
      if (cancelled) return;
      if (!res.success || !res.data) {
        setError(res.error || 'Failed to load prospects.');
        setRows([]);
        setTotal(0);
      } else {
        setRows(res.data);
        setTotal(typeof res.total === 'number' ? res.total : res.data.length);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [stageId, status, showArchived, showConverted, debouncedSearch, page]);

  const stageMap = useMemo(() => {
    const m = new Map<string, Stage>();
    for (const s of stages) m.set(s.id, s);
    return m;
  }, [stages]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      {/* Filters bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, or business…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={stageId}
          onChange={(e) => setStageId(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All stages</option>
          {stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.display_name}
            </option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StageStatus | '')}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="rounded border-gray-300"
          />
          Archived
        </label>

        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={showConverted}
            onChange={(e) => setShowConverted(e.target.checked)}
            className="rounded border-gray-300"
          />
          Converted
        </label>

        <button
          onClick={() => setPage((p) => p)}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Table */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Name</th>
                  <th className="px-3 py-2 text-left font-medium">Email</th>
                  <th className="px-3 py-2 text-left font-medium">Business</th>
                  <th className="px-3 py-2 text-left font-medium">Stage</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Time in stage</th>
                  <th className="px-3 py-2 text-left font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                      <RefreshCw className="w-4 h-4 inline-block animate-spin mr-2" />
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-gray-500 italic">
                      No prospects match these filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const stage = stageMap.get(r.current_stage_id);
                    const lifecycleBadge = r.converted_at
                      ? { label: 'Converted', cls: 'bg-green-100 text-green-800' }
                      : r.archived_at
                      ? { label: 'Archived', cls: 'bg-gray-200 text-gray-700' }
                      : null;
                    return (
                      <tr
                        key={r.id}
                        onClick={() => onOpenProspect(r.id)}
                        className="cursor-pointer hover:bg-blue-50"
                      >
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {r.applicant_first_name} {r.applicant_last_name}
                          {lifecycleBadge && (
                            <span
                              className={`ml-2 inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${lifecycleBadge.cls}`}
                            >
                              {lifecycleBadge.label}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{r.applicant_email}</td>
                        <td className="px-3 py-2 text-gray-700">
                          {r.legal_business_name || (
                            <span className="text-gray-400 italic">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {stage?.display_name || (
                            <span className="text-gray-400 italic">unknown</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <StatusPill status={r.current_stage_status} />
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {formatTimeInStage(r.entered_current_stage_at)}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {formatDate(r.created_at)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-gray-50 px-3 py-2 border-t border-gray-200 flex items-center justify-between text-sm text-gray-700">
            <span>
              {total === 0
                ? '0 results'
                : `${page * PAGE_SIZE + 1}-${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 hover:bg-white"
              >
                Previous
              </button>
              <span className="text-gray-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 hover:bg-white"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: StageStatus }) {
  const config: Record<StageStatus, { label: string; cls: string }> = {
    awaiting_client: { label: 'Awaiting client', cls: 'bg-amber-100 text-amber-800' },
    awaiting_admin: { label: 'Awaiting us', cls: 'bg-blue-100 text-blue-800' },
    awaiting_external: { label: 'Awaiting review', cls: 'bg-purple-100 text-purple-800' },
    rejected_needs_fix: { label: 'Needs fix', cls: 'bg-red-100 text-red-800' },
    complete: { label: 'Complete', cls: 'bg-green-100 text-green-800' },
  };
  const c = config[status] || config.awaiting_admin;
  return (
    <span
      className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${c.cls}`}
    >
      {c.label}
    </span>
  );
}

function formatTimeInStage(enteredAt: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(enteredAt).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}
